/**
 * Document extraction service.
 *
 * Wraps the Railway routes that ran as the `extract-invoice` and
 * `extract-ticket` Supabase edge functions. Both routes accept a
 * base64-encoded PDF in the body and return a structured extraction
 * envelope. Response shape matches the existing `ExtractionResult` /
 * `TicketExtractionResult` types so call sites consume it as before.
 */

import { ApiError, apiPost } from "@/lib/apiClient";

export type ExtractAIProvider = "openai";

export interface ExtractRequest {
  pdfBase64: string;
  aiProvider?: ExtractAIProvider;
}

interface ExtractEnvelopeBase {
  success: boolean;
  extraction_method?: "native_text" | "ocr" | "vision";
  ai_provider?: string;
  page_count?: number;
  confidence?: number;
  data?: Record<string, unknown>;
  debug?: { processing_time_ms: number };
  prompt_sent?: string;
  error?: string;
}

function toErrorEnvelope(err: unknown): ExtractEnvelopeBase {
  const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
  return { success: false, error: message };
}

export const extractService = {
  /**
   * Extract invoice fields from a PDF (base64). Mirrors the prior
   * `supabase.functions.invoke('extract-invoice', { body: ... })` shape.
   */
  async extractInvoice<T extends ExtractEnvelopeBase = ExtractEnvelopeBase>(
    request: ExtractRequest,
  ): Promise<T> {
    try {
      return await apiPost<T>("/api/extract/invoice", request);
    } catch (err) {
      return toErrorEnvelope(err) as T;
    }
  },

  /**
   * Extract field-ticket fields from a PDF (base64). Mirrors the prior
   * `supabase.functions.invoke('extract-ticket', { body: ... })` shape.
   */
  async extractTicket<T extends ExtractEnvelopeBase = ExtractEnvelopeBase>(
    request: ExtractRequest,
  ): Promise<T> {
    try {
      return await apiPost<T>("/api/extract/ticket", request);
    } catch (err) {
      return toErrorEnvelope(err) as T;
    }
  },
};
